import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, ArrowsLeftRight } from "@phosphor-icons/react";
import axios from "axios";
import { API } from "@/App";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";
import { playerToSlug, compareUrl } from "@/lib/playerSlug";

const PC = { QB: "#ff6b6b", RB: "#4ecdc4", WR: "#45b7d1", TE: "#f9ca24" };

export default function CompareIndex() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posFilter, setPosFilter] = useState("ALL");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/fantasycalc/values?numQbs=2&numTeams=12&ppr=1`);
        setPlayers(res.data.filter(p => p.player?.position && ["QB","RB","WR","TE"].includes(p.player.position) && !p.player.name?.includes("Pick")).slice(0, 50));
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const filtered = posFilter === "ALL" ? players : players.filter(p => p.player.position === posFilter);

  // Build popular comparison pairs from top players
  const pairs = [];
  const bucket = filtered.slice(0, 20);
  for (let i = 0; i < bucket.length && pairs.length < 30; i++) {
    for (let j = i + 1; j < bucket.length && j <= i + 5 && pairs.length < 30; j++) {
      pairs.push({ a: bucket[i], b: bucket[j] });
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title="Dynasty Player Comparisons: Head-to-Head Value Breakdowns — Dynasty Trade Maker" description="Compare any two dynasty players head-to-head. SF vs 1QB values, age, trend, Championship Rate, and AI analysis. 3,000+ comparison pages." path="/compare" />
      <PublicNav />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mb-2">PLAYER <span className="text-[#ccff00]">COMPARISONS</span></h1>
        <p className="text-sm text-[#a3a3a3] mb-6">Head-to-head dynasty value breakdowns for 3,000+ player pairs. Pick a position to see the most popular matchups.</p>

        <div className="flex gap-1 mb-6">
          {["ALL","QB","RB","WR","TE"].map(pos => (
            <button key={pos} onClick={() => setPosFilter(pos)} className={`px-3 py-1.5 text-[10px] font-bold uppercase rounded-sm transition-colors ${posFilter === pos ? "bg-[#ccff00] text-black" : "border border-[#262626] text-[#888] hover:border-[#444]"}`}>{pos}</button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-2">{[...Array(8)].map((_,i) => <div key={i} className="h-12 skeleton rounded-sm" />)}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {pairs.map((pair, i) => {
              const a = pair.a.player;
              const b = pair.b.player;
              const url = compareUrl(a.name, b.name);
              const imgA = a.sleeperId ? `https://sleepercdn.com/content/nfl/players/thumb/${a.sleeperId}.jpg` : null;
              const imgB = b.sleeperId ? `https://sleepercdn.com/content/nfl/players/thumb/${b.sleeperId}.jpg` : null;
              return (
                <Link key={i} to={url} className="flex items-center gap-2 sm:gap-3 p-3 border border-[#262626] bg-[#171717] hover:border-[#ccff00]/30 no-underline transition-colors rounded-sm">
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
                    {imgA && <img src={imgA} alt="" className="w-7 h-7 sm:w-8 sm:h-8 rounded-sm object-cover shrink-0" onError={e => e.target.style.display='none'} />}
                    <div className="min-w-0">
                      <p className="text-[11px] sm:text-xs text-white font-medium leading-tight">{a.name}</p>
                      <p className="text-[9px] text-[#555]">{pair.a.value?.toLocaleString()}</p>
                    </div>
                  </div>
                  <ArrowsLeftRight size={12} className="text-[#444] shrink-0" />
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0 justify-end">
                    <div className="min-w-0 text-right">
                      <p className="text-[11px] sm:text-xs text-white font-medium leading-tight">{b.name}</p>
                      <p className="text-[9px] text-[#555]">{pair.b.value?.toLocaleString()}</p>
                    </div>
                    {imgB && <img src={imgB} alt="" className="w-7 h-7 sm:w-8 sm:h-8 rounded-sm object-cover shrink-0" onError={e => e.target.style.display='none'} />}
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Research & Tools */}
        <div className="mt-10 border-t border-[#262626] pt-8">
          <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-4">MAKE BETTER COMPARISONS WITH DATA</h2>
          <p className="text-sm text-[#a3a3a3] leading-relaxed mb-5">{"Comparing players isn\u2019t just about value — it\u2019s about what kind of player wins championships. Our research from 9,983 real title teams tells you which profiles actually matter."}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <Link to="/research/where-champions-put-their-value" className="group border border-[#262626] bg-[#171717] p-3 rounded-sm no-underline hover:border-[#ccff00]/30 transition-colors">
              <p className="text-xs font-bold text-white mb-0.5 group-hover:text-[#ccff00] transition-colors">Positional Value Blueprint</p>
              <p className="text-[10px] text-[#888]">WR 35.7%, RB 30.1%, QB 22.5%, TE 11.7%.</p>
            </Link>
            <Link to="/research/when-running-backs-fall-off" className="group border border-[#262626] bg-[#171717] p-3 rounded-sm no-underline hover:border-[#ccff00]/30 transition-colors">
              <p className="text-xs font-bold text-white mb-0.5 group-hover:text-[#ccff00] transition-colors">When RBs Fall Off</p>
              <p className="text-[10px] text-[#888]">~47% of peak at 27. Factor age into your comparison.</p>
            </Link>
            <Link to="/research/why-elite-wrs-are-safest" className="group border border-[#262626] bg-[#171717] p-3 rounded-sm no-underline hover:border-[#ccff00]/30 transition-colors">
              <p className="text-xs font-bold text-white mb-0.5 group-hover:text-[#ccff00] transition-colors">Why Elite WRs Are Safest</p>
              <p className="text-[10px] text-[#888]">WRs decline gently while RBs cliff. The safest bet.</p>
            </Link>
          </div>
          <div className="flex flex-wrap gap-3 text-[10px]">
            <Link to="/trade-calculator" className="text-[#ccff00] no-underline hover:underline">Trade Calculator</Link>
            <Link to="/trades" className="text-[#ccff00] no-underline hover:underline">Real Trade Database</Link>
            <Link to="/rankings" className="text-[#ccff00] no-underline hover:underline">Full Rankings</Link>
            <Link to="/research" className="text-[#ccff00] no-underline hover:underline">33 Research Articles</Link>
            <Link to="/research/championship-dna-players" className="text-[#ccff00] no-underline hover:underline">Championship DNA</Link>
            <Link to="/guide" className="text-[#ccff00] no-underline hover:underline">Dynasty Guides</Link>
          </div>
        </div>

        <div className="mt-8 text-center text-[10px] text-[#444] space-x-3">
          <Link to="/players" className="hover:text-[#a3a3a3] no-underline">Players</Link>
          <Link to="/rankings" className="hover:text-[#a3a3a3] no-underline">Rankings</Link>
          <Link to="/trades" className="hover:text-[#a3a3a3] no-underline">Trades</Link>
        </div>
      </div>
    </div>
  );
}
